import {
  AnteriorPosteriorPonticDesignLabels,
  InterproximalContactDesignLabels,
  NoOcclusalClearanceLabels,
  OcclusalContactTightnessLabels,
  OcclusalStainingLabels,
  ProximalContactTightnessLabels,
  TranslatedItemPreferencesName,
} from "../types/constants";
import { ItemType, ItemMaterials, ItemPreferencesName } from "../types/enums";
import type { ItemPreferences } from "../types/interfaces";

export type ItemPreferenceValues = {
  OCCLUSAL_STAINING: string;
  OCCLUSAL_CONTACT_TIGHTNESS: string;
  NO_OCCLUSAL_CLEARANCE: string;
  PROXIMAL_CONTACT_TIGHTNESS: string;
  INTERPROXIMAL_CONTACT_DESIGN: string;
  ANTERIOR_POSTERIOR_PONTIC_DESIGN: string;
};

const ITEM_PREFERENCE_CHOICES: Record<
  ItemPreferencesName,
  { value: string; label: string }[]
> = {
  [ItemPreferencesName.OCCLUSAL_STAINING]: OcclusalStainingLabels,
  [ItemPreferencesName.OCCLUSAL_CONTACT_TIGHTNESS]:
    OcclusalContactTightnessLabels,
  [ItemPreferencesName.PROXIMAL_CONTACT_TIGHTNESS]:
    ProximalContactTightnessLabels,
  [ItemPreferencesName.NO_OCCLUSAL_CLEARANCE]: NoOcclusalClearanceLabels,
  [ItemPreferencesName.INTERPROXIMAL_CONTACT_DESIGN]:
    InterproximalContactDesignLabels,
  [ItemPreferencesName.ANTERIOR_POSTERIOR_PONTIC_DESIGN]:
    AnteriorPosteriorPonticDesignLabels,
};

export const getItemPreferenceLabel = (
  preferenceType: ItemPreferencesName,
  options: ItemPreferenceValues,
  shouldTranslate: boolean
): { label: string; value: string } => {
  const choices = ITEM_PREFERENCE_CHOICES[preferenceType];
  if (!choices) {
    throw new Error(`Unsupported preference type: ${preferenceType}`);
  }
  const value = options[preferenceType];

  return {
    label:
      TranslatedItemPreferencesName[preferenceType][
        shouldTranslate ? "EN" : "FR"
      ],
    value: shouldTranslate
      ? value
      : (choices.find(choice => choice.value === value)?.label ?? value),
  };
};

export const translatedItemPreferences = (
  preferenceType: ItemPreferencesName,
  options: ItemPreferenceValues,
  shouldTranslate: boolean
): string => {
  const { label, value } = getItemPreferenceLabel(
    preferenceType,
    options,
    shouldTranslate
  );
  return `- ${label} = ${value}`;
};

export const getItemPreferenceValues = (
  itemPreferences: ItemPreferences | undefined
): ItemPreferenceValues => {
  const preferences = itemPreferences?.set ?? itemPreferences;

  return {
    OCCLUSAL_STAINING: preferences?.occlusalStaining ?? "",
    OCCLUSAL_CONTACT_TIGHTNESS: preferences?.occlusalContactTightness ?? "",
    PROXIMAL_CONTACT_TIGHTNESS: preferences?.proximalContactTightness ?? "",
    INTERPROXIMAL_CONTACT_DESIGN: preferences?.interproximalContactDesign ?? "",
    ANTERIOR_POSTERIOR_PONTIC_DESIGN:
      preferences?.anteriorPosteriorPonticDesign ?? "",
    NO_OCCLUSAL_CLEARANCE: preferences?.noOcclusalClearance ?? "",
  };
};

export const translateItemType = (
  itemType: ItemType | typeof MULTIPLE | ""
): string => {
  switch (itemType) {
    case ItemType.INLAY:
      return "Inlay";

    case ItemType.ONLAY:
      return "Onlay";

    case ItemType.OVERLAY:
      return "Overlay";

    case ItemType.CROWN:
      return "Crown";

    case ItemType.BRIDGE:
      return "Bridge";

    case ItemType.INLAY_CORE:
      return "Post & Core";

    case ItemType.RICHMOND_CROWN:
      return "Post & Core IN ONE PIECE (Richmond Crown)";

    case ItemType.SPLINTED_CROWN:
      return "Splinted crowns";

    case ItemType.IMPLANT:
      return "Abutment";

    case ItemType.IMPLANT_CROWN:
      return "Implant Crown";

    case ItemType.BRIDGE_IMPLANT:
      return "Implant Bridge";

    case ItemType.BRIDGE_PONTIC:
      return "Pontic";

    case ItemType.SPLINTED_CROWN_IMPLANT:
      return "Splinted implant crowns";

    case ItemType.PARTIAL_DENTURE:
      return "Partial denture";

    case ItemType.NIGHT_GUARD:
      return "Night Guard";

    case ItemType.BLEACHING_TRAY:
      return "Bleaching Tray";

    case ItemType.RETAINER:
      return "Retainer";

    case ItemType.FULL_DENTURE:
      return "Full Denture";

    case ItemType.IMMEDIATE_DENTURE:
      return "Immediate Denture";

    case ItemType.FULL_DENTURE_WAX_BITE:
      return "Full Denture Wax Bite Rim";

    case ItemType.RETENTION_WIRE:
      return "Retention Wire";

    case ItemType.SPACE_MAINTAINER:
      return "Space Maintainer";

    case ItemType.WAX_UP:
      return "Wax Up";

    case ItemType.CUSTOM_TRAY:
      return "Custom Tray";

    case ItemType.ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS:
      return "Additional impression for removable prosthesis";

    case ItemType.ADDING_TEETH_ON_EXISTING_DENTURE:
      return "Adding teeth on existing denture";

    case ItemType.DENTURE_REPAIR:
      return "Denture repair";

    case ItemType.ORTHO_MODEL:
      return "Orto model";

    case ItemType.WAX_BITE:
      return "Wax Bite";

    default:
      return itemType;
  }
};

export const translateItemMaterial = (itemMaterial: ItemMaterials): string => {
  switch (itemMaterial) {
    case ItemMaterials.METAL_NON_PRECIOUS:
      return "Metal";
    case ItemMaterials.PFM_NON_PRECIOUS:
      return "Porcelain Fused To Metal";
    case ItemMaterials.PFM_NON_PRECIOUS_POST_AND_CORE:
      return "Opaque";
    case ItemMaterials.PFM_PRECIOUS:
      return "Porcelain Fused to Precious Metal";
    case ItemMaterials.PORCELAIN_FUSED_ZIRCONIA:
      return "Porcelain Fused to Zirconia";
    case ItemMaterials.ZIRCONIA_FULL:
      return "Full Zirconia";
    case ItemMaterials.ZIRCONIA_MULTILAYER:
      return "Multilayer Full Zirconia";
    case ItemMaterials.EMAX_FULL:
      return "Emax";
    case ItemMaterials.EMAX_MULTILAYER:
      return "3D Emax Multilayer ";
    case ItemMaterials.COMPOSITE_NO_METAL:
      return "Composite";
    case ItemMaterials.TITANIUM:
      return "Titanium ";
    case ItemMaterials.TI_BASE:
      return "Ti-base";
    case ItemMaterials.COBALT_CHROME_METALLIC_FRAME:
      return "Co-Cr Framework ";
    case ItemMaterials.TITANIUM_FRAME:
      return "Titanium Framework";
    case ItemMaterials.VALPLAST:
      return "Valplast";
    case ItemMaterials.ACRYLIC:
      return "Acrylic";
    case ItemMaterials.ZIRCONIA:
      return "Zirconia";
    case ItemMaterials.NIGHT_GUARD_HARD_MATERIAL:
      return "Hard Material";
    case ItemMaterials.NIGHT_GUARD_SOFT_MATERIAL:
      return "Soft Material ";
    case ItemMaterials.NIGHT_GUARD_MEDIUM_MATERIAL:
      return "Medium Material ";

    default:
      return itemMaterial;
  }
};

export const translateInlayCoreMaterial = (inlayCoreMaterial?: string) => {
  switch (inlayCoreMaterial) {
    case "METAL_NON_PRECIOUS":
      return "Metal Post & Core";
    case "PFM_NON_PRECIOUS_POST_AND_CORE":
      return "Opaque Post & Core";
    default:
      return inlayCoreMaterial;
  }
};
